import React from "react";

// ---------------------------------------------------------------------------
// The KATLAS Operating Model
// Central KATLAS engine + four-stage clockwise loop:
//   Design → Prove → Reuse → Scale (→ back to Design)
// Desktop: 3-column grid with tall centre KATLAS panel + SVG protocol lines
// Mobile: KATLAS panel first, then the four stages stacked vertically
// ---------------------------------------------------------------------------

const STAGES = [
  {
    id: "design",
    n: "01",
    slot: "top-left",
    title: "Design with partners",
    kicker: "Cierge Labs",
    body:
      "Map the operational journey, actors, authority boundary, evidence boundary and measurable outcome.",
    cta: { label: "Open Cierge Labs", href: "https://labs.katlastechnology.com/" },
  },
  {
    id: "prove",
    n: "02",
    slot: "top-right",
    title: "Prove role-to-role protocols",
    kicker: "Authority and proof travel where needed.",
    body:
      "Test real partner journeys where authority, evidence, proof and accountable action need to travel.",
    cta: { label: "See Authority Storybook", href: "https://authority-journey.katlastechnology.com/" },
  },
  {
    id: "reuse",
    n: "03",
    slot: "bottom-right",
    title: "Create reusable Storybooks",
    kicker: "Templates and sector patterns.",
    body:
      "Turn proven journeys into templates and sector patterns that can be adapted without starting from zero.",
    cta: { label: "Browse Journey Storybooks", href: "https://labs.katlastechnology.com/journeys" },
  },
  {
    id: "scale",
    n: "04",
    slot: "bottom-left",
    title: "Scale governed operations",
    kicker: "Equip the organisation, not only the pilot.",
    body:
      "Enable business development, technology, CEO, CTO and compliance teams to introduce AI safely within a defined operating model.",
    cta: { label: "Discuss a pilot", href: "https://www.katlastechnology.com/contact/" },
  },
];

const stageBySlot = STAGES.reduce((acc, s) => {
  acc[s.slot] = s;
  return acc;
}, {});

const StageCard = ({ stage }) => (
  <a
    href={stage.cta.href}
    data-testid={`operating-model-stage-${stage.id}`}
    className="relative bg-[#040F1B] border border-white/[0.08] p-5 lg:p-6 flex flex-col h-full hover:border-[#0066FF]/40 hover:bg-[#061322] transition-colors group"
  >
    <div className="flex items-baseline gap-3">
      <span className="font-display text-3xl leading-none text-[#0066FF]">
        {stage.n}
      </span>
      <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        Stage
      </span>
    </div>
    <h3 className="mt-4 font-display text-[17px] sm:text-lg text-white leading-snug">
      {stage.title}
    </h3>
    <p className="mt-2 text-xs mono uppercase tracking-[0.14em] text-[#0066FF]">
      {stage.kicker}
    </p>
    <p className="mt-3 text-sm text-slate-300 leading-relaxed flex-1">
      {stage.body}
    </p>
    <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between">
      <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300 group-hover:text-white transition-colors">
        {stage.cta.label}
      </span>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="text-[#0066FF] group-hover:translate-x-0.5 transition-transform"
      >
        <path d="M7 17 17 7M8 7h9v9" />
      </svg>
    </div>
  </a>
);

const CoreEngine = () => (
  <div
    data-testid="operating-model-core"
    className="relative bg-[#020B14] border border-[#0066FF]/30 p-6 lg:p-7 flex flex-col justify-center items-center text-center h-full overflow-hidden"
    style={{
      backgroundImage:
        "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,102,255,0.10), transparent 70%)",
    }}
  >
    <div className="absolute inset-0 bg-grid-fine opacity-40 pointer-events-none" />
    <div className="relative">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
        KATLAS
      </div>
      <div className="font-display text-xl sm:text-2xl lg:text-[26px] text-white mt-3 leading-tight">
        CAROPS infrastructure{" "}
        <span className="text-slate-400">for governed AI</span>
      </div>
      <div className="mt-5 h-px w-16 bg-[#0066FF]/40 mx-auto" />
      <div className="mt-5 flex flex-col gap-1.5 text-[11px] mono uppercase tracking-[0.22em] text-slate-300">
        <span>Custody</span>
        <span>Authority</span>
        <span className="text-[#EAB308]">Receipts</span>
        <span>Role-to-role</span>
      </div>
    </div>
  </div>
);

export const OperatingModel = () => {
  return (
    <section
      id="operating-model"
      data-testid="section-operating-model"
      className="relative py-24 lg:py-32 border-t border-white/[0.06] bg-[#020B14]"
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
            The KATLAS Operating Model
          </div>
          <h2
            data-testid="operating-model-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.06]"
          >
            From AI ambition{" "}
            <span className="text-slate-400">to governed operation.</span>
          </h2>
          <p
            data-testid="operating-model-lede"
            className="mt-6 text-sm sm:text-base text-slate-300 leading-relaxed"
          >
            KATLAS is CAROPS infrastructure for organisations that need
            people, AI, systems and partner organisations to act across real
            operational boundaries. At its centre are four enduring controls:
          </p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-300 leading-relaxed">
            <li>
              <span className="text-white">Custody</span> keeps evidence, data
              and context with the party responsible for it.
            </li>
            <li>
              <span className="text-white">Authority</span> governs who can
              request, decide, approve, refuse, delegate or escalate.
            </li>
            <li>
              <span className="text-white">Receipts</span> create verifiable
              proof of what happened, when and under whose authority.
            </li>
            <li>
              <span className="text-white">Role-to-role protocols</span>{" "}
              enable trusted actions to move between organisations without
              defaulting to blanket access or centralised data custody.
            </li>
          </ul>
        </div>

        {/* Desktop: 3-column loop layout */}
        <div className="hidden lg:block relative">
          {/* SVG protocol connectors */}
          <svg
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
          >
            <defs>
              <marker
                id="opm-arrow"
                markerWidth="6"
                markerHeight="6"
                refX="5"
                refY="3"
                orient="auto"
              >
                <path d="M0,0 L6,3 L0,6 z" fill="#0066FF" opacity="0.55" />
              </marker>
            </defs>
            {/* Top edge: Design → Prove */}
            <line
              x1="18"
              y1="8"
              x2="82"
              y2="8"
              stroke="#0066FF"
              strokeOpacity="0.35"
              strokeWidth="0.35"
              strokeDasharray="1.5,1.5"
              markerEnd="url(#opm-arrow)"
              className="opm-flow"
            />
            {/* Right edge: Prove → Reuse */}
            <line
              x1="92"
              y1="22"
              x2="92"
              y2="78"
              stroke="#0066FF"
              strokeOpacity="0.35"
              strokeWidth="0.35"
              strokeDasharray="1.5,1.5"
              markerEnd="url(#opm-arrow)"
              className="opm-flow"
              style={{ animationDelay: "0.6s" }}
            />
            {/* Bottom edge: Reuse → Scale (right to left) */}
            <line
              x1="82"
              y1="92"
              x2="18"
              y2="92"
              stroke="#0066FF"
              strokeOpacity="0.35"
              strokeWidth="0.35"
              strokeDasharray="1.5,1.5"
              markerEnd="url(#opm-arrow)"
              className="opm-flow"
              style={{ animationDelay: "1.2s" }}
            />
            {/* Left edge: Scale → Design (bottom to top) */}
            <line
              x1="8"
              y1="78"
              x2="8"
              y2="22"
              stroke="#0066FF"
              strokeOpacity="0.35"
              strokeWidth="0.35"
              strokeDasharray="1.5,1.5"
              markerEnd="url(#opm-arrow)"
              className="opm-flow"
              style={{ animationDelay: "1.8s" }}
            />
          </svg>

          <style>{`
            .opm-flow {
              stroke-dasharray: 1.5 1.5;
              animation: opm-dash 6s linear infinite;
            }
            @keyframes opm-dash {
              from { stroke-dashoffset: 0; }
              to   { stroke-dashoffset: -30; }
            }
          `}</style>

          <div
            className="relative grid gap-5"
            style={{
              gridTemplateColumns: "1fr 1.15fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gridTemplateAreas: `
                "design katlas prove"
                "scale  katlas reuse"
              `,
            }}
          >
            <div style={{ gridArea: "design" }}>
              <StageCard stage={stageBySlot["top-left"]} />
            </div>
            <div style={{ gridArea: "katlas" }}>
              <CoreEngine />
            </div>
            <div style={{ gridArea: "prove" }}>
              <StageCard stage={stageBySlot["top-right"]} />
            </div>
            <div style={{ gridArea: "scale" }}>
              <StageCard stage={stageBySlot["bottom-left"]} />
            </div>
            <div style={{ gridArea: "reuse" }}>
              <StageCard stage={stageBySlot["bottom-right"]} />
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3 text-[11px] mono uppercase tracking-[0.22em] text-slate-500">
            <span>Loop</span>
            <span className="text-slate-700" aria-hidden>·</span>
            <span className="text-slate-300">Design</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Prove</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Reuse</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Scale</span>
            <span className="text-slate-500" aria-hidden>⟳</span>
          </div>
        </div>

        {/* Mobile / tablet: engine first, stages stacked below */}
        <div className="lg:hidden space-y-4">
          <CoreEngine />
          {STAGES.map((s) => (
            <StageCard key={s.id} stage={s} />
          ))}
          <div className="pt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] mono uppercase tracking-[0.22em] text-slate-500">
            <span>Loop</span>
            <span className="text-slate-700" aria-hidden>·</span>
            <span className="text-slate-300">Design</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Prove</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Reuse</span>
            <span className="text-[#0066FF]" aria-hidden>→</span>
            <span className="text-slate-300">Scale</span>
            <span className="text-slate-500" aria-hidden>⟳</span>
          </div>
        </div>
      </div>
    </section>
  );
};
